import { Transaction, User } from '@apis/index'
import { Table, TableUser, Status } from '@components'
import { useEffect, useState } from 'react'

interface UserType {
  id: string
  name: string
  email: string
  image?: string
  phoneNo?: string
}

interface TransactionType {
  id: string
  userId: string
  amount: number
  mode: string
  status: string
  createdAt: string
  user?: UserType
  expiresIn?: string
}

const AdminTransactionManagement = () => {
  const [transactions, setTransactions] = useState<TransactionType[]>([])
  const [users, setUsers] = useState<UserType[]>([])
  const [params, setParams] = useState({ page: 1, limit: 10 })
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchUsers = async () => {
    try {
      const res = await User.list('users', {
        limit: 1000,
        page: 1,
      })
      setUsers(res.items || [])
    } catch (err) {
      console.error('Failed to fetch users', err)
    }
  }

  const fetchTransactions = async () => {
    setLoading(true)
    try {
      const res = await Transaction.get(params)
      const transactionItems = res?.items ?? []
      const merged = transactionItems.map((transaction) => {
        const user = users.find((u) => u.id === transaction.userId)
        return {
          ...transaction,
          user,
        }
      })
      setTransactions(merged)
      setTotalPages(res.totalPages || 1)
    } catch (err) {
      console.error('Failed to fetch transactions', err)
    } finally {
      setLoading(false)
    }
  }

  const getDaysRemaining = (expiresAt?: string) => {
    if (!expiresAt) return 'N/A'

    try {
      const now = new Date()
      const expiry = new Date(expiresAt)

      if (isNaN(expiry.getTime())) return 'Invalid date'

      const diffTime = expiry.getTime() - now.getTime()

      if (diffTime <= 0) return 'Expired'

      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor(
        (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      )

      if (diffDays > 0) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ${diffHours} hour${diffHours !== 1 ? 's' : ''}`
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`
    } catch (error) {
      console.error('Error calculating expiry:', error)
      return 'N/A'
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if (users.length > 0) {
      fetchTransactions()
    }
  }, [params, users])

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (item: TransactionType) => (
        <TableUser
          hasAvatar
          avatar={item.user?.image}
          label={item.user?.name ?? 'N/A'}
          secondaryLabel={item.user?.phoneNo ?? 'N/A'}
        />
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (item: TransactionType) => `Rs. ${item.amount}`,
    },

    {
      title: 'Status',
      key: 'status',
      render: (item: TransactionType) => <Status status={item.status} />,
    },
    {
      title: 'Subscription Date',
      key: 'createdAt',
      render: (item: TransactionType) =>
        new Date(item.createdAt).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
    },
    {
      title: 'Expiry Date',
      key: 'expiresIn',
      render: (item: TransactionType) =>
        item.expiresIn
          ? new Date(item.expiresIn).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : 'N/A',
    },
    {
      title: 'Days Remaining',
      key: 'daysRemaining',
      render: (item: TransactionType) => getDaysRemaining(item.expiresIn),
    },
  ]

  return (
    <div>
      <Table
        tableTitle="Transaction History"
        columns={columns}
        data={transactions}
        loading={loading}
        paginationProps={{
          totalPages,
          perPage: params.limit,
          activePage: params.page,
          handleOnClick: (pageNo: number) => {
            setParams((prev) => ({ ...prev, page: pageNo }))
          },
        }}
      />
    </div>
  )
}

export default AdminTransactionManagement
